/**
 * OrganizationProvider - Organization Context for Reconflux
 *
 * Manages the user's organizations and tracks the currently selected one.
 * Supports multi-org users (accountants, consultants) who can switch between orgs.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './AuthProvider'
import type { Organization, OrgRole } from '../types/database'
import {
  findOrganizationById,
  pickCurrentOrganization,
  type OrganizationWithRole,
} from './organizationHelpers'

// =============================================================================
// Types
// =============================================================================

interface OrganizationState {
  organizations: OrganizationWithRole[]
  currentOrganization: OrganizationWithRole | null
  isLoading: boolean
  error: string | null
}

interface OrganizationContextType extends OrganizationState {
  // Actions
  switchOrganization: (orgId: string) => void
  /** Re-fetch organizations without replacing the current route with its loading screen. */
  refreshOrganizations: () => Promise<OrganizationWithRole[]>
  createOrganization: (name: string, address?: string, trackedVats?: string[]) => Promise<{ data: Organization | null; error: string | null }>

  // Computed — current role + capability flags.
  // Policy source of truth: documents/role-permissions-matrix.md
  hasOrganizations: boolean
  role: OrgRole | null
  isOwner: boolean
  /** Org admin (issue #843): every owner capability but deleting an integration. */
  isAdmin: boolean
  isAccountant: boolean
  /** Owners and admins; an admin manages non-owner memberships only. */
  canManageMembers: boolean
  /** Owner-and-admin surfaces: billing, team, org settings, integration config. */
  canManageOrganization: boolean
  /** Deleting (disconnecting) an integration stays owner-only. */
  canDeleteIntegrations: boolean
  /** Owners, admins and accountants may delete invoices; members may not. */
  canDeleteInvoices: boolean
}

// =============================================================================
// Constants
// =============================================================================

const CURRENT_ORG_KEY = 'reconflux_current_org_id'

// =============================================================================
// Context
// =============================================================================

const OrganizationContext = createContext<OrganizationContextType | null>(null)

// =============================================================================
// Provider
// =============================================================================

interface OrganizationProviderProps {
  children: ReactNode
}

export function OrganizationProvider({ children }: OrganizationProviderProps) {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth()

  const [state, setState] = useState<OrganizationState>({
    organizations: [],
    currentOrganization: null,
    isLoading: true,
    error: null,
  })

  // Fetch user's organizations. Returns the freshly fetched list so callers
  // (e.g. createOrganization) can select from it directly instead of reading
  // back through a memoized closure over the pre-fetch state (issue #372).
  const fetchOrganizations = useCallback(async ({ silent = false } = {}): Promise<OrganizationWithRole[]> => {
    if (!user) {
      setState({
        organizations: [],
        currentOrganization: null,
        isLoading: false,
        error: null,
      })
      return []
    }

    try {
      // Route guards use isLoading only for the initial organization lookup.
      // User-initiated refreshes must keep the current screen mounted.
      if (!silent) {
        setState(prev => ({ ...prev, isLoading: true, error: null }))
      }

      // Fetch organizations with user's role
      const { data: memberships, error } = await supabase
        .from('organization_members')
        .select(`
          role,
          organization:organizations (*)
        `)
        .eq('user_id', user.id)

      if (error) throw error

      // Transform data to include role
      const orgsWithRole: OrganizationWithRole[] = (memberships || [])
        .filter(m => m.organization)
        .map(m => ({
          ...(m.organization as Organization),
          role: m.role,
        }))

      // Determine current organization — restore from localStorage, else first
      const savedOrgId = localStorage.getItem(CURRENT_ORG_KEY)
      const currentOrg = pickCurrentOrganization(orgsWithRole, savedOrgId)

      setState({
        organizations: orgsWithRole,
        currentOrganization: currentOrg,
        isLoading: false,
        error: null,
      })

      // Persist current org
      if (currentOrg) {
        localStorage.setItem(CURRENT_ORG_KEY, currentOrg.id)
      }

      return orgsWithRole
    } catch (err) {
      console.error('Error fetching organizations:', err)
      setState(prev => ({
        ...prev,
        isLoading: silent ? prev.isLoading : false,
        error: err instanceof Error ? err.message : 'Failed to load organizations',
      }))
      return []
    }
  }, [user])

  // Initial fetch when user changes
  useEffect(() => {
    if (!authLoading) {
      fetchOrganizations()
    }
  }, [fetchOrganizations, authLoading, isAuthenticated])

  // Switch organization
  const switchOrganization = useCallback((orgId: string) => {
    const org = findOrganizationById(state.organizations, orgId)

    if (org) {
      setState(prev => ({ ...prev, currentOrganization: org }))
      localStorage.setItem(CURRENT_ORG_KEY, orgId)
    }
  }, [state.organizations])

  // Refresh organizations
  const refreshOrganizations = useCallback(async () => {
    return fetchOrganizations({ silent: true })
  }, [fetchOrganizations])

  // Create new organization
  const createOrganization = useCallback(async (
    name: string,
    address?: string,
    trackedVats?: string[]
  ): Promise<{ data: Organization | null; error: string | null }> => {
    try {
      const { data, error } = await supabase.rpc('create_org_with_owner', {
        org_name: name,
        org_address: address,
        org_tracked_vats: trackedVats || [],
      })

      if (error) throw error

      // Refresh organizations and select the new one out of the fresh list —
      // not via switchOrganization, whose memoized closure would still see
      // the pre-refresh list and miss the org that was just created (#372).
      const freshOrgs = await fetchOrganizations()
      const newOrg = data ? findOrganizationById(freshOrgs, data.id) : null

      if (!newOrg) {
        // The org was created, but the refresh failed (or didn't yet reflect
        // it) — leave currentOrganization/organizations untouched rather than
        // reporting success while the context still points at the previous
        // org. Callers must not navigate as if the new workspace is active.
        return {
          data: null,
          error: 'Organization created, but we could not switch to it. Please refresh the page and select it from the organization switcher.',
        }
      }

      setState(prev => ({ ...prev, currentOrganization: newOrg }))
      localStorage.setItem(CURRENT_ORG_KEY, newOrg.id)

      return { data, error: null }
    } catch (err) {
      console.error('Error creating organization:', err)
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to create organization',
      }
    }
  }, [fetchOrganizations])

  // Computed values — see documents/role-permissions-matrix.md
  const hasOrganizations = state.organizations.length > 0
  const role = state.currentOrganization?.role ?? null
  const isOwner = role === 'owner'
  const isAdmin = role === 'admin'
  const isAccountant = role === 'accountant'
  const canManageMembers = isOwner || isAdmin
  const canManageOrganization = isOwner || isAdmin // billing, settings, integration config
  const canDeleteIntegrations = isOwner
  const canDeleteInvoices = isOwner || isAdmin || isAccountant

  const value: OrganizationContextType = {
    ...state,
    switchOrganization,
    refreshOrganizations,
    createOrganization,
    hasOrganizations,
    role,
    isOwner,
    isAdmin,
    isAccountant,
    canManageMembers,
    canManageOrganization,
    canDeleteIntegrations,
    canDeleteInvoices,
  }

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  )
}

// =============================================================================
// Hook
// =============================================================================

// eslint-disable-next-line react-refresh/only-export-components
export function useOrganization(): OrganizationContextType {
  const context = useContext(OrganizationContext)

  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider')
  }

  return context
}

// Convenience hook for getting just the current org ID (for API calls)
// eslint-disable-next-line react-refresh/only-export-components
export function useCurrentOrgId(): string | null {
  const { currentOrganization } = useOrganization()
  return currentOrganization?.id ?? null
}

// Export context for advanced use cases
export { OrganizationContext }
